/**
 * PRICEBOOK MOCK DATA
 *
 * Centralized mock data for pricebooks including entries.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { Pricebook, PricebookEntry } from '../../types/index';

/**
 * DEFAULT PRICEBOOK RECORDS
 * Standard: one system-wide default (no accountId). Firm pricebooks: accountId + partnerId + useStandardPricebook.
 */
export const defaultPricebookRecords: Pricebook[] = [
  {
    id: 'pb_001',
    name: 'Standard Pricebook',
    description: 'Default pricebook with standard pricing for all products',
    isStandard: true,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModifiedDate: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'pb_002',
    name: 'Acme Law Firm',
    description: 'Uses default pricebook',
    isStandard: false,
    isActive: true,
    createdDate: '2024-02-01T10:00:00Z',
    lastModifiedDate: '2024-02-01T10:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_001',
    useStandardPricebook: true,
    partnerId: 'contact_acc_001_1'
  },
  {
    id: 'pb_003',
    name: 'Global Partners LLC',
    description: 'Custom pricing for Global Partners',
    isStandard: false,
    isActive: true,
    createdDate: '2024-03-01T09:00:00Z',
    lastModifiedDate: '2024-03-01T09:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_002',
    useStandardPricebook: false,
    partnerId: 'contact_acc_002_1'
  },
  {
    id: 'pb_004',
    name: 'Summit Financial Group',
    description: 'Custom pricing for Summit Financial',
    isStandard: false,
    isActive: true,
    createdDate: '2024-04-01T08:00:00Z',
    lastModifiedDate: '2024-04-01T08:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_003',
    useStandardPricebook: false,
    partnerId: 'contact_acc_003_1'
  },
  {
    id: 'pb_005',
    name: 'Riverside Legal Associates',
    description: 'Uses default pricebook',
    isStandard: false,
    isActive: true,
    createdDate: '2024-05-15T11:00:00Z',
    lastModifiedDate: '2024-05-15T11:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_004',
    useStandardPricebook: true,
    partnerId: 'contact_acc_004_1'
  },
  {
    id: 'pb_006',
    name: 'Metro Credit Advisors',
    description: 'Custom volume pricing for Metro Credit',
    isStandard: false,
    isActive: true,
    createdDate: '2024-06-10T14:00:00Z',
    lastModifiedDate: '2024-08-01T09:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_005',
    useStandardPricebook: false,
    partnerId: 'contact_acc_005_1'
  },
  {
    id: 'pb_007',
    name: 'Pacific Coast Financial',
    description: 'Custom pricing for Pacific Coast partners',
    isStandard: false,
    isActive: true,
    createdDate: '2024-07-20T10:00:00Z',
    lastModifiedDate: '2024-09-15T16:00:00Z',
    createdBy: 'admin',
    accountId: 'acc_006',
    useStandardPricebook: false,
    partnerId: 'contact_acc_006_1'
  }
];

/**
 * DEFAULT PRICEBOOK ENTRIES
 */
export const defaultPricebookEntries: PricebookEntry[] = [
  // Standard Pricebook entries (same as product default prices)
  { id: 'pbe_001', pricebookId: 'pb_001', productId: 'prod_001', unitPrice: 99.00, currency: 'USD', isActive: true },
  { id: 'pbe_002', pricebookId: 'pb_001', productId: 'prod_002', unitPrice: 149.00, currency: 'USD', isActive: true },
  { id: 'pbe_003', pricebookId: 'pb_001', productId: 'prod_003', unitPrice: 79.00, currency: 'USD', isActive: true },
  { id: 'pbe_004', pricebookId: 'pb_001', productId: 'prod_004', unitPrice: 89.00, currency: 'USD', isActive: true },
  { id: 'pbe_005', pricebookId: 'pb_001', productId: 'prod_005', unitPrice: 15.00, currency: 'USD', isActive: true },
  { id: 'pbe_006', pricebookId: 'pb_001', productId: 'prod_006', unitPrice: 35.00, currency: 'USD', isActive: true },
  { id: 'pbe_007', pricebookId: 'pb_001', productId: 'prod_007', unitPrice: 19.99, currency: 'USD', isActive: true },
  { id: 'pbe_008', pricebookId: 'pb_001', productId: 'prod_008', unitPrice: 49.99, currency: 'USD', isActive: true },
  
  // Global Partners LLC (pb_003) custom entries
  { id: 'pbe_009', pricebookId: 'pb_003', productId: 'prod_001', unitPrice: 79.00, currency: 'USD', isActive: true },
  { id: 'pbe_010', pricebookId: 'pb_003', productId: 'prod_002', unitPrice: 119.00, currency: 'USD', isActive: true },
  { id: 'pbe_011', pricebookId: 'pb_003', productId: 'prod_003', unitPrice: 59.00, currency: 'USD', isActive: true },
  { id: 'pbe_012', pricebookId: 'pb_003', productId: 'prod_004', unitPrice: 69.00, currency: 'USD', isActive: true },
  { id: 'pbe_013', pricebookId: 'pb_003', productId: 'prod_005', unitPrice: 12.00, currency: 'USD', isActive: true },
  { id: 'pbe_014', pricebookId: 'pb_003', productId: 'prod_006', unitPrice: 28.00, currency: 'USD', isActive: true },
  // Summit Financial Group (pb_004) custom entries
  { id: 'pbe_015', pricebookId: 'pb_004', productId: 'prod_001', unitPrice: 69.00, currency: 'USD', isActive: true },
  { id: 'pbe_016', pricebookId: 'pb_004', productId: 'prod_002', unitPrice: 99.00, currency: 'USD', isActive: true },
  { id: 'pbe_017', pricebookId: 'pb_004', productId: 'prod_003', unitPrice: 49.00, currency: 'USD', isActive: true },
  { id: 'pbe_018', pricebookId: 'pb_004', productId: 'prod_004', unitPrice: 59.00, currency: 'USD', isActive: true },
  // Metro Credit Advisors (pb_006) custom entries
  { id: 'pbe_019', pricebookId: 'pb_006', productId: 'prod_001', unitPrice: 84.00, currency: 'USD', isActive: true, effectiveDate: '2024-06-01T00:00:00Z' },
  { id: 'pbe_020', pricebookId: 'pb_006', productId: 'prod_002', unitPrice: 129.00, currency: 'USD', isActive: true, effectiveDate: '2024-06-01T00:00:00Z' },
  { id: 'pbe_021', pricebookId: 'pb_006', productId: 'prod_003', unitPrice: 64.00, currency: 'USD', isActive: true },
  { id: 'pbe_022', pricebookId: 'pb_006', productId: 'prod_004', unitPrice: 74.00, currency: 'USD', isActive: true },
  { id: 'pbe_023', pricebookId: 'pb_006', productId: 'prod_005', unitPrice: 13.00, currency: 'USD', isActive: true },
  { id: 'pbe_024', pricebookId: 'pb_006', productId: 'prod_006', unitPrice: 30.00, currency: 'USD', isActive: true },
  // Pacific Coast Financial (pb_007) custom entries
  { id: 'pbe_025', pricebookId: 'pb_007', productId: 'prod_001', unitPrice: 74.00, currency: 'USD', isActive: true, effectiveDate: '2024-07-20T00:00:00Z', expirationDate: '2025-12-31T23:59:59Z' },
  { id: 'pbe_026', pricebookId: 'pb_007', productId: 'prod_002', unitPrice: 109.00, currency: 'USD', isActive: true },
  { id: 'pbe_027', pricebookId: 'pb_007', productId: 'prod_003', unitPrice: 54.00, currency: 'USD', isActive: true },
  { id: 'pbe_028', pricebookId: 'pb_007', productId: 'prod_004', unitPrice: 64.00, currency: 'USD', isActive: true },
  { id: 'pbe_029', pricebookId: 'pb_007', productId: 'prod_005', unitPrice: 14.00, currency: 'USD', isActive: true },
  { id: 'pbe_030', pricebookId: 'pb_007', productId: 'prod_006', unitPrice: 32.00, currency: 'USD', isActive: true },
  { id: 'pbe_031', pricebookId: 'pb_007', productId: 'prod_007', unitPrice: 17.99, currency: 'USD', isActive: true }
];

/**
 * PERSISTED STORAGE
 */
export const persistedPricebookRecords = createPersistedMockData(
  'crm_pricebook_records',
  defaultPricebookRecords
);

export const persistedPricebookEntries = createPersistedMockData(
  'crm_pricebook_entries',
  defaultPricebookEntries
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockPricebookRecords = defaultPricebookRecords;
export const mockPricebookEntries = defaultPricebookEntries;
